import type { Dispatch, UnknownAction } from 'redux';
import { broadcastSessionPurged } from '@/utility/auth/sessionBroadcast';
import { resetTokenRefreshLock } from '@/utility/api/tokenRefreshLock';

/** RTK action type — avoids importing authSlice (breaks apiUtils circular dep). */
const RESET_AUTH_STATE_ACTION = { type: 'auth/resetAuthState' } as const;

/** Auth-related keys written outside redux-persist. */
export const AUTH_LOCAL_STORAGE_KEYS = [
  'ACCESS_TOKEN',
  'REFRESH_TOKEN',
] as const;

export type PurgeSessionOptions = {
  dispatch?: Dispatch<UnknownAction>;
  /** Skip cross-tab BroadcastChannel when handling a remote purge. */
  skipBroadcast?: boolean;
  /** Hard redirect after local state is cleared. */
  redirectToLogin?: boolean;
};

const clearAuthLocalStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  AUTH_LOCAL_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });
};

/**
 * Clears all client auth state in one safe invocation:
 * - Redux auth slice reset
 * - Token localStorage keys removed
 * - redux-persist auth snapshot purged
 * - Other tabs notified via BroadcastChannel
 */
export const purgeSession = async ({
  dispatch,
  skipBroadcast = false,
  redirectToLogin = false,
}: PurgeSessionOptions = {}): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  resetTokenRefreshLock();
  clearAuthLocalStorage();

  const storeModule = await import('@/redux/store');
  const activeDispatch = dispatch ?? storeModule.store.dispatch;

  activeDispatch(RESET_AUTH_STATE_ACTION);

  await storeModule.persistor.purge();

  if (!skipBroadcast) {
    broadcastSessionPurged();
  }

  if (redirectToLogin && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};
